/**
 * Glass-styled tab strip for the learner experience.
 * Wraps React Aria Tabs with the learner glass language —
 * translucent active pill, subtle surface border, smooth spring animation.
 *
 * Panels are optional — omit `content` on tabs to use as a controlled selector.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  Tabs as AriaTabs,
  TabList as AriaTabList,
  Tab as AriaTab,
  TabPanel as AriaTabPanel,
  type Key,
} from 'react-aria-components';
import { motion } from 'motion/react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TabItem {
  id: string;
  label: string;
  content?: ReactNode | (() => ReactNode);
  isDisabled?: boolean;
}

interface TabsProps {
  tabs?: TabItem[];
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
  className?: string;
  style?: CSSProperties;
}

// ─── Scroll fade masks ───────────────────────────────────────────────────────

const FADE = 36;

interface ScrollState {
  left: boolean;
  right: boolean;
}

function fadeMask({ left, right }: ScrollState): CSSProperties | undefined {
  if (!left && !right) return undefined;
  const mask =
    left && right
      ? `linear-gradient(to right, transparent, black ${FADE}px, black calc(100% - ${FADE}px), transparent)`
      : left
        ? `linear-gradient(to right, transparent, black ${FADE}px)`
        : `linear-gradient(to left, transparent, black ${FADE}px)`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function Tabs({
  tabs = [],
  selectedKey: controlledKey,
  onSelectionChange,
  className,
  style,
}: TabsProps) {
  const pillId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  // Selection state (controlled or uncontrolled)
  const [internalKey, setInternalKey] = useState(tabs[0]?.id);
  const activeKey = controlledKey ?? internalKey;

  const handleChange = useCallback(
    (key: Key) => {
      setInternalKey(String(key));
      onSelectionChange?.(String(key));
    },
    [onSelectionChange]
  );

  // ── Scroll state ──────────────────────────────────────────────────────────

  const [scroll, setScroll] = useState<ScrollState>({ left: false, right: false });

  const syncScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setScroll({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    syncScroll();
    el.addEventListener('scroll', syncScroll, { passive: true });
    const ro = new ResizeObserver(syncScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', syncScroll);
      ro.disconnect();
    };
  }, [syncScroll]);

  // ── Scroll selected tab into view ─────────────────────────────────────────

  useEffect(() => {
    if (!activeKey) return;
    const list = listRef.current;
    if (!list) return;
    requestAnimationFrame(() => {
      const tab = list.querySelector<HTMLElement>('[data-selected]');
      if (!tab) return;
      const lr = list.getBoundingClientRect();
      const tr = tab.getBoundingClientRect();
      if (tr.left >= lr.left && tr.right <= lr.right) return;
      list.scrollTo({
        left: tab.offsetLeft + tab.offsetWidth / 2 - list.clientWidth / 2,
        behavior: 'smooth',
      });
    });
  }, [activeKey]);

  if (!tabs.length) return null;

  const hasPanels = tabs.some((t) => t.content !== undefined);

  return (
    <AriaTabs
      selectedKey={activeKey}
      onSelectionChange={handleChange}
      className={className}
      style={style}
    >
      {/* ── Tab list ───────────────────────────────────────────────────────── */}
      <AriaTabList
        ref={listRef}
        style={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          padding: '4px',
          borderRadius: 9999,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-surface-border)',
          backdropFilter: 'blur(12px) saturate(1.2)',
          WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
          boxShadow: 'inset 0 0.5px 0 var(--color-surface-highlight), 0 1px 2px rgba(0,0,0,0.03)',
          scrollbarWidth: 'none',
          ...fadeMask(scroll),
        }}
      >
        {tabs.map((t) => {
          const isActive = t.id === activeKey;

          return (
            <AriaTab
              key={t.id}
              id={t.id}
              isDisabled={t.isDisabled}
              style={{
                position: 'relative',
                flexShrink: 0,
                appearance: 'none',
                border: 'none',
                background: 'transparent',
                padding: '9px 18px',
                borderRadius: 9999,
                fontSize: 15,
                fontWeight: isActive ? 600 : 500,
                fontFamily: 'var(--font-sans)',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                cursor: t.isDisabled ? 'not-allowed' : 'pointer',
                color: t.isDisabled
                  ? 'var(--color-text-muted)'
                  : isActive
                    ? 'var(--color-text)'
                    : 'var(--color-text-muted)',
                opacity: t.isDisabled ? 0.5 : 1,
                transition: 'color 0.15s',
                outline: 'none',
              }}
            >
              {/* Animated active pill */}
              {isActive && (
                <motion.div
                  layoutId={`ltab-pill-${pillId}`}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 9999,
                    background: 'var(--color-surface-solid)',
                    border: '1px solid var(--color-surface-border)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 0.5px 1px rgba(0,0,0,0.03)',
                  }}
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
              <span style={{ position: 'relative' }}>{t.label}</span>
            </AriaTab>
          );
        })}
      </AriaTabList>

      {/* ── Panels ─────────────────────────────────────────────────────────── */}
      {hasPanels &&
        tabs.map((t) => (
          <AriaTabPanel key={t.id} id={t.id} style={{ paddingTop: 16, outline: 'none' }}>
            {t.content !== undefined
              ? typeof t.content === 'function'
                ? t.content()
                : t.content
              : null}
          </AriaTabPanel>
        ))}
    </AriaTabs>
  );
}
